import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'

import cx from 'classnames'

import { Box, Flex, Text } from '@driftt/dds'

import { RadialEngagementScore } from 'components/EngagementRadialScore'
import { EngagementScoreFlames } from 'components/EngagementScoreFlames'
import { CurrentUserContext } from 'context/currentUserContext'
import { AccountActivity } from 'modules/accountActivity/domain'
import { ReactComponent as SparklesIcon } from 'svgs/sparkles.svg'
import { accountPath, accountsPath } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

import ExplainAIEngagement from './ExplainAIEngagement'

import utilityStyles from '../../styles/utility.module.css'
import styles from './styles.module.css'

interface EngagementScoreProps {
  score: number
  prevScore?: number
  radial?: boolean
  className?: string
}

export const scoreToDescription = (score: number): string => {
  switch (score) {
    case 1:
      return 'Not engaged'
    case 2:
      return 'Somewhat engaged'
    case 3:
      return 'Engaged'
    case 4:
      return 'Very engaged'
    case 5:
      return 'Extremely engaged'
    default:
      return 'Not engaged'
  }
}

const onMoreClick = (history: any) => (activity: AccountActivity) => {
  history.push({
    pathname: accountPath(activity.accountId),
    state: {
      companyData: activity.companyData,
      navigatedFrom: 'Target accounts',
      navigatedFromUrl: accountsPath,
      accountEngagementScore: activity.normalizedEngagementScore
    },
    hash: history.location.hash
  })
}

export const EngagementScoreOverview: React.FC<EngagementScoreProps> = ({
  score,
  prevScore,
  radial = false,
  className
}) => {
  if (score < 0) {
    score = 0
  }

  return (
    <div className={cx(styles.engagementScoreOverviewContainer, className)}>
      {radial ? (
        <RadialEngagementScore score={Math.min(Math.round(score), 5)} prevScore={prevScore} />
      ) : (
        <>
          <div className={cx(styles.engagementScoreNumber, utilityStyles.semiBoldWeight)}>
            {Math.min(Math.round(score), 5)}
          </div>
          <div className={styles.engagementScoreDescriptionContainer}>
            <EngagementScoreFlames score={Math.min(Math.round(score), 5)} />
            <div className={styles.subTextStyle}>
              {scoreToDescription(Math.min(Math.round(score), 5))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export const ExplainSection: React.FC<{
  accountActivity: AccountActivity
  limit?: number
  showMore?: boolean
  truncate: boolean
}> = ({ accountActivity, limit, showMore = true, truncate }) => {
  const currentUser = useContext(CurrentUserContext)
  const history = useHistory()

  const onClick = onMoreClick(history)

  return (
    <>
      <ExplainAIEngagement truncate={truncate} limit={limit} accountActivity={accountActivity} />
      {limit && showMore ? (
        <Box
          css={{
            padding: '$1 0',
            textAlign: 'center'
          }}
          onClick={() => {
            segmentTrack(TrackType.AI_ENGAGEMENT_SCORE_TOOLTIP_VIEW_ACTIVITY_CLICK, {
              accountId: accountActivity.accountId,
              orgId: currentUser?.orgId,
              userId: currentUser?.id
            })
            onClick(accountActivity)
          }}
          data-ocweb-id="EngagementScoreFullBreakdown">
          <Text
            css={{
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
            variant="metadata">
            Click to view all activity
          </Text>
        </Box>
      ) : (
        <></>
      )}
    </>
  )
}

export interface ExplainCardProps {
  includeHeader?: boolean
  includeOverview?: boolean
  accountActivity: AccountActivity
  limit?: number
  showMore?: boolean
  truncate?: boolean
}

export const ExplainContents: React.FC<ExplainCardProps> = ({
  accountActivity,
  limit,
  includeHeader = true,
  includeOverview = true,
  showMore,
  truncate = true
}) => {
  const companyName =
    accountActivity.companyData?.name ?? accountActivity?.mostRecentActivity?.companyData?.name

  return (
    <>
      {includeHeader && (
        <>
          <div className={cx(styles.headerStyle, utilityStyles.semiBoldWeight)}>
            Engagement score
          </div>
          <div className={cx(styles.subTextStyle)}>{companyName ?? ''}</div>
        </>
      )}
      {includeOverview && (
        <EngagementScoreOverview score={accountActivity.normalizedEngagementScore} />
      )}
      <Box css={{ borderBottom: '1px solid $gray3' }}>
        <Flex
          alignItems="center"
          gap="half"
          css={{
            width: '100%',
            paddingBottom: '$1'
          }}>
          <SparklesIcon />
          <Text variant="metadata">Contributing factors determined by Drift AI</Text>
        </Flex>
      </Box>

      <ExplainSection
        accountActivity={accountActivity}
        limit={limit}
        showMore={showMore}
        truncate={truncate}
      />
    </>
  )
}

export const ExplainHoverCard: React.FC<ExplainCardProps> = props => {
  return (
    <div className={cx(styles.explainCard)} onClick={e => e.stopPropagation()}>
      <ExplainContents {...props} />
    </div>
  )
}

export default ExplainContents
